import { useNavigate } from "react-router-dom";
import {
  ArrowRight, MonitorSmartphone, MapPin, Users, Star, TabletSmartphone, Clock, BarChart3,
  Camera, FileText, Settings, MessageCircle, Tag, Monitor, FilePen, CircleCheck, Rocket,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const BENEFITS = [
  { icon: MonitorSmartphone, t: "Παρουσία σε App & Web", d: "Η επιχείρησή σου προβάλλεται σε κινητό και υπολογιστή." },
  { icon: MapPin, t: "Σε βρίσκουν τοπικά", d: "Εμφανίζεσαι στις σωστές κατηγορίες και στην περιοχή που δραστηριοποιείσαι." },
  { icon: Users, t: "Νέο κοινό", d: "Γονείς, παιδιά, νέοι και ενήλικες που αναζητούν δραστηριότητες και υπηρεσίες." },
  { icon: Star, t: "Πλήρης παρουσία", d: "Φωτογραφίες, πληροφορίες, δραστηριότητες, επικοινωνία και τοποθεσία." },
];

const STATS = [
  { icon: TabletSmartphone, t: "APP + WEB", d: "Διπλή ψηφιακή παρουσία" },
  { icon: Clock, t: "24/7", d: "Η επιχείρησή σου διαθέσιμη στην αναζήτηση" },
  { icon: BarChart3, t: "1 ΠΛΑΤΦΟΡΜΑ", d: "Δραστηριότητες & υπηρεσίες συγκεντρωμένες σε ένα σημείο" },
];

const FEATURES = [
  { icon: Camera, t: "Λογότυπο & φωτογραφίες", d: "Ανάδειξε τον χώρο, τα τμήματα και τις δραστηριότητές σου." },
  { icon: MapPin, t: "Τοποθεσία στον χάρτη", d: "Εύκολη πρόσβαση με ένα κλικ." },
  { icon: FileText, t: "Αναλυτική παρουσίαση", d: "Περιγραφή, ηλικίες, παροχές και ό,τι χρειάζεται ο ενδιαφερόμενος." },
  { icon: MessageCircle, t: "Άμεση επικοινωνία", d: "Τηλέφωνο, email, φόρμα ή μήνυμα." },
  { icon: Settings, t: "Δραστηριότητες / υπηρεσίες", d: "Ξεχωριστή προβολή για κάθε τμήμα ή υπηρεσία." },
  { icon: Tag, t: "Προβολή στη σωστή κατηγορία", d: "Σε βρίσκουν αυτοί που σε χρειάζονται πραγματικά." },
  { icon: Clock, t: "Ωράριο", d: "Ενημέρωσε το κοινό για το ωράριο λειτουργίας σου." },
  { icon: Monitor, t: "Πρόσβαση από App & Web", d: "Συνεχής παρουσία σε κινητό και υπολογιστή." },
];

const STEPS = [
  { n: "01", icon: FilePen, t: "Εκδηλώνεις ενδιαφέρον", d: "Συμπληρώνεις τη φόρμα ή επικοινωνείς μαζί μας." },
  { n: "02", icon: Settings, t: "Δημιουργούμε την παρουσία σου", d: "Η ομάδα του SkillCity καταχωρίζει και διαμορφώνει την καρτέλα σου." },
  { n: "03", icon: CircleCheck, t: "Εμφανίζεσαι στο SkillCity", d: "Η επιχείρησή σου είναι πλέον διαθέσιμη σε App & Web." },
  { n: "04", icon: BarChart3, t: "Σε ανακαλύπτει νέο κοινό", d: "Περισσότερη προβολή, περισσότερες επικοινωνίες και νέες ευκαιρίες." },
];

const WHO = [
  ["sports", "Αθλητικοί Σύλλογοι"], ["education", "Εκπαίδευση & Μελέτη"], ["languages", "Ξένες Γλώσσες"],
  ["dance", "Χορός"], ["music", "Μουσική"], ["arts", "Εικαστικά"], ["stem", "STEM & Ρομποτική"],
  ["nature", "Δραστηριότητες & Φύση"], ["all", "Υπηρεσίες για Όλους"],
];

const EDGE_FADE = {
  WebkitMaskImage: "linear-gradient(to right, transparent, #000 7%, #000 93%, transparent), linear-gradient(to bottom, transparent, #000 7%, #000 93%, transparent)",
  WebkitMaskComposite: "source-in",
  maskImage: "linear-gradient(to right, transparent, #000 7%, #000 93%, transparent), linear-gradient(to bottom, transparent, #000 7%, #000 93%, transparent)",
  maskComposite: "intersect",
};

const IconBubble = ({ icon: Icon, size = "h-14 w-14", iconSize = 26 }) => (
  <div className={`${size} shrink-0 rounded-full bg-sky-100 text-primary grid place-items-center`}>
    <Icon size={iconSize} strokeWidth={2.2} />
  </div>
);

const InterestButton = ({ onClick, variant = "solid", testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-3 h-14 px-10 rounded-full font-extrabold text-sm tracking-wide uppercase shadow-lg transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-xl ${
      variant === "solid" ? "bg-primary text-white" : "bg-white text-primary"
    }`}
  >
    Εκδήλωσε ενδιαφέρον <ArrowRight size={18} />
  </button>
);

const Hero = ({ go }) => (
  <section className="relative overflow-hidden" data-testid="partner-hero">
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-4 grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 text-slate-800 text-sm font-semibold">Γίνε Συνεργάτης</span>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-[1.1]" data-testid="partner-title">
          Ενισχύστε την παρουσία σας.<br />Προσεγγίστε νέο κοινό.
        </h1>
        <p className="mt-5 text-slate-700 text-base sm:text-lg leading-relaxed max-w-xl">
          Γίνε μέρος του <b>SkillCity</b> και κάνε τις υπηρεσίες σου πιο εύκολα προσβάσιμες σε γονείς, νέους και ενήλικες που αναζητούν δραστηριότητες και υπηρεσίες στην πόλη τους.
        </p>
        <div className="mt-8"><InterestButton onClick={go} testid="partner-hero-interest-btn" /></div>
      </div>
      <img src="/partner/hero_devices.jpg" alt="SkillCity σε κινητό και υπολογιστή" style={EDGE_FADE} className="w-full" data-testid="partner-hero-image" />
    </div>
  </section>
);

const Benefits = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-benefits">
    {BENEFITS.map((b) => (
      <div key={b.t} className="bg-white rounded-2xl shadow-md p-4 flex gap-3">
        <IconBubble icon={b.icon} size="h-12 w-12" iconSize={24} />
        <div>
          <h3 className="font-display font-extrabold text-base text-slate-900 leading-tight">{b.t}</h3>
          <p className="mt-1.5 text-[13px] text-slate-600 leading-snug">{b.d}</p>
        </div>
      </div>
    ))}
  </section>
);

const Stats = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-5" data-testid="partner-stats">
    <div className="rounded-2xl bg-sky-100/80 px-6 py-6 grid md:grid-cols-3 gap-6 md:divide-x divide-slate-300">
      {STATS.map((s) => (
        <div key={s.t} className="flex items-center gap-5 md:px-6">
          <s.icon size={50} strokeWidth={1.8} className="text-primary shrink-0" />
          <div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{s.t}</div>
            <div className="text-sm text-slate-600 leading-snug">{s.d}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Presence = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 grid lg:grid-cols-[2fr_3fr] gap-8 items-center" data-testid="partner-presence">
    <img src="/partner/phone_card.jpg" alt="Καρτέλα επιχείρησης στο SkillCity" style={EDGE_FADE} className="w-full max-w-md mx-auto" />
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Η δική σου ψηφιακή παρουσία</h2>
      <p className="mt-2 text-slate-600 text-base md:text-lg">Μια ολοκληρωμένη καρτέλα για την επιχείρησή σου, διαμορφωμένη από την ομάδα του SkillCity.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-6">
        {FEATURES.map((f) => (
          <div key={f.t} className="flex gap-4">
            <IconBubble icon={f.icon} size="h-11 w-11" iconSize={20} />
            <div>
              <h3 className="font-bold text-primary leading-tight">{f.t}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-snug">{f.d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10" data-testid="partner-how">
    <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8">
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Πώς λειτουργεί;</h2>
      <p className="mt-1 text-slate-600">Απλά βήματα, χωρίς να χρειάζεται να ασχοληθείς με τεχνικά θέματα.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {STEPS.map((s, i) => (
          <div key={s.n} className="relative">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary text-white grid place-items-center font-display font-extrabold text-lg">{s.n}</div>
              <IconBubble icon={s.icon} size="h-12 w-12" iconSize={22} />
              {i < STEPS.length - 1 && <ArrowRight size={24} className="hidden lg:block ml-auto mr-2 text-slate-400" />}
            </div>
            <h3 className="mt-4 font-bold text-slate-900 leading-tight">{s.t}</h3>
            <p className="mt-1 text-sm text-slate-600 leading-snug">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Who = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10" data-testid="partner-who">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Ποιοι μπορούν να συμμετέχουν</h2>
    <p className="mt-1 text-slate-600">Επιχειρήσεις, σύλλογοι και οργανισμοί από όλο το φάσμα δραστηριοτήτων για παιδιά και ενήλικες.</p>
    <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
      {WHO.map(([k, label]) => (
        <div key={k} className="bg-white rounded-2xl shadow-md p-3 flex flex-col items-center text-center">
          <img src={`/partner/ic_${k}.jpg`} alt={label} className="h-14 w-auto mix-blend-multiply" />
          <span className="mt-2 text-xs font-bold text-slate-800 leading-tight">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

const Banner = ({ go }) => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 mb-12" data-testid="partner-cta">
    <div className="rounded-3xl bg-gradient-to-r from-primary to-sky-400 text-white px-6 sm:px-10 py-8 flex flex-col md:flex-row items-center gap-6">
      <Rocket size={64} strokeWidth={1.6} className="shrink-0" />
      <div className="flex-1 text-center md:text-left">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold">Η επόμενη αναζήτηση μπορεί να οδηγεί σε εσένα.</h2>
        <p className="mt-2 text-white/90">Έλα στο SkillCity και κάνε την επιχείρηση ή τον οργανισμό σου μέρος ενός σύγχρονου τοπικού οικοσυστήματος.</p>
      </div>
      <InterestButton onClick={go} variant="light" testid="partner-cta-button" />
    </div>
  </section>
);

const PartnerPage = () => {
  const navigate = useNavigate();
  const go = () => navigate("/contact", { state: { partner: true } });
  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-page">
      <Navbar />
      <main>
        <Hero go={go} />
        <Benefits />
        <Stats />
        <Presence />
        <HowItWorks />
        <Who />
        <Banner go={go} />
      </main>
      <Footer />
    </div>
  );
};

export default PartnerPage;
